import { definePlugin } from '../../framework';
import type { FieldSuggestion, TransformState, TransformStep } from '../../types';
import { confirmation, fieldExpectation } from '../../utils/confirmation';
import { isPlaceholder, PLACEHOLDER_PATTERN } from '../../utils/placeholders';

const replaceAll = (source: unknown, pattern: string, target: string, flags = 'g') => ({
  '$utils.regexp.replace': [source, [pattern, flags], target],
});

// The engine trims a value wrapped like this (the "Remove Leading and
// Trailing Spaces" block does the same).
const trimmed = (source: unknown) => [[source]];

type Cleanup = {
  apply: (value: string) => string;
  snippet: (source: unknown) => unknown;
};

/**
 * Ways to clean a value, mildest first. Each predicts its result locally and
 * builds the snippet that does the same in the engine, the way the editor's
 * transformer blocks do. A cleaned value is only offered when something
 * confirms it (see cleanupCandidates).
 */
export const CLEANUPS: Cleanup[] = [
  {
    // Line breaks and runs of spaces become one space.
    apply: (value) => value.replace(/\s+/g, ' ').trim(),
    snippet: (source) => trimmed(replaceAll(source, '\\s+', ' ')),
  },
  {
    // No spaces at all, as in a reference written in groups.
    apply: (value) => value.replace(/\s+/g, ''),
    snippet: (source) => replaceAll(source, '\\s+', ''),
  },
  {
    // Spaces and symbols removed, like the "Remove Special Characters" block.
    apply: (value) => value.replace(/[\s,/()\-.]/g, ''),
    snippet: (source) => replaceAll(source, '[\\s,/()\\-.]', ''),
  },
  {
    // Loose punctuation at the start or the end.
    apply: (value) => value.replace(/^[\s.,;:-]+|[\s.,;:-]+$/g, ''),
    snippet: (source) => replaceAll(source, '^[\\s.,;:-]+|[\\s.,;:-]+$', ''),
  },
];

/**
 * Cleaned versions of a suggestion's value that something confirms: the
 * value the plugin expected, another suggestion, or an enum value the field
 * accepts. Each cleaned value once, from the mildest cleanup that gives it.
 */
export const cleanupCandidates = (
  state: TransformState,
  suggestion: FieldSuggestion,
  others: FieldSuggestion[],
  field: ReturnType<typeof fieldExpectation>,
): TransformState[] => {
  if (typeof state.value !== 'string' || suggestion.iterator === true) return [];
  const value = state.value;
  const confirmed = confirmation(suggestion, others, field, value.trim());

  const seen = new Set<string>([value]);
  const candidates: TransformState[] = [];
  for (const cleanup of CLEANUPS) {
    const cleaned = cleanup.apply(value);
    if (cleaned === '' || seen.has(cleaned)) continue;
    seen.add(cleaned);
    if (confirmed(cleaned)) {
      candidates.push({ snippet: cleanup.snippet(state.snippet), value: cleaned });
    }
  }
  return candidates;
};

/** Offers the confirmed cleaned versions next to the value as it is. */
export const cleanupStep: TransformStep = {
  name: 'cleanup',
  stage: 'cleanup',
  mode: 'add',
  apply: (state, { entry, suggestion, suggestions }) =>
    isPlaceholder(state.value)
      ? []
      : cleanupCandidates(state, suggestion, suggestions, fieldExpectation(entry)),
};

/**
 * A placeholder ("volgt", "N/A") is never the field's value, so it is
 * replaced: the snippet gives an empty value where the document has a
 * placeholder, and the value itself on documents that have one.
 */
export const placeholderStep: TransformStep = {
  name: 'cleanup',
  stage: 'cleanup',
  mode: 'replace',
  apply: (state, { suggestion }) =>
    suggestion.iterator !== true && isPlaceholder(state.value)
      ? [{ snippet: replaceAll(state.snippet, PLACEHOLDER_PATTERN, '', 'i'), value: '' }]
      : [],
};

export const textCleanup = definePlugin<{}>({
  name: 'Cleanup',
  version: '0.1.0',
  description:
    'Replaces Remove Special Characters, Remove Leading and Trailing Spaces and Replace Symbols: offers a cleaned value when something confirms it, and empties placeholders like "N/A" or "TBD".',
  initialState: {},

  bootstrap: () => {},

  transformSteps: () => [placeholderStep, cleanupStep],
});
